import { useState } from 'react'

import { Button, Card, Field, Input, Note } from '../components/ui'
import { api } from '../lib/api'
import { useSession } from '../lib/session'

const PASSWORD_MIN = 12

/** `/account` — change your own password. Every **other** session is revoked;
 *  this one stays alive, because signing someone out of the page they just used
 *  teaches them to avoid changing passwords. */
export function Account() {
  const { me } = useSession()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (next !== confirm) {
      setError('The Two New Passwords Do Not Match.')
      return
    }
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const result = await api.changePassword(current, next)
      setMessage(result.detail)
      setCurrent('')
      setNext('')
      setConfirm('')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could Not Change the Password')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-4" style={{ maxWidth: '32rem' }}>
      <Card title="Account">
        <dl className="m-0 text-sm">
          <div className="flex gap-4 py-1">
            <dt style={{ color: 'var(--text-secondary)', minWidth: '7rem' }}>Email</dt>
            <dd className="m-0">{me?.email}</dd>
          </div>
          <div className="flex gap-4 py-1">
            <dt style={{ color: 'var(--text-secondary)', minWidth: '7rem' }}>Role</dt>
            <dd className="m-0">{me?.role === 'admin' ? 'Administrator' : 'User'}</dd>
          </div>
          <div className="flex gap-4 py-1">
            <dt style={{ color: 'var(--text-secondary)', minWidth: '7rem' }}>Access</dt>
            <dd className="m-0">
              {me?.role === 'admin'
                ? 'Everything, Including Revenue'
                : me?.grants.length
                  ? me.grants.join(', ')
                  : 'No Dashboards Yet'}
            </dd>
          </div>
        </dl>
      </Card>

      <Card title="Change Password">
        {message && <Note tone="good">{message}</Note>}
        {error && <Note tone="error">{error}</Note>}
        <form onSubmit={submit} noValidate>
          <Field label="Current Password">
            <Input type="password" value={current} required autoComplete="current-password"
                   onChange={(event) => setCurrent(event.target.value)} />
          </Field>
          <Field label="New Password" hint={`At least ${PASSWORD_MIN} characters.`}>
            <Input type="password" value={next} required minLength={PASSWORD_MIN}
                   autoComplete="new-password"
                   onChange={(event) => setNext(event.target.value)} />
          </Field>
          <Field label="Confirm New Password">
            <Input type="password" value={confirm} required minLength={PASSWORD_MIN}
                   autoComplete="new-password"
                   onChange={(event) => setConfirm(event.target.value)} />
          </Field>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Change Password'}
          </Button>
        </form>
      </Card>
    </div>
  )
}
